import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import {
    alpha, Box,
    IconButton,
    TableCell,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'
import { useDispatch, useSelector } from 'react-redux';

import { toLocaleStringIn } from '../../util/general';
import {
    Broker_OrderType,
    Order,
    OrderStatus,
    OrderType,
    productToJSON,
    TransactionType,
} from '../../proto/generated/order';

import { cancelOrder } from '../../services/apis/orders';
import { QuickTradeData, setQuickOrderData, updateOrderHistory } from '../../reducer/orders.reducer';
import commonActions from '../../actions/common.actions'
import { selectPrimaryTradingAccount, selectBrokers, } from '../../selectors/user'
import { getSecurityAsync, getSecurityKey } from '../../util/security';
import OrderStatusDisplay from '../orderStatus/orderStatusDisplay';
import OrderActionsMenu from './OrderActionsMenu'
import OrderInfoDialog from './OrderInfoDialog'
import OrderHistoryDialog from './OrderHistoryDialog'
import TradeHistoryDialog from './TradeHistoryDialog'
import { selectGroupBy, selectPositions } from '../../selectors/positions';
import { GroupedPosition } from '../../reducer/positions.reducer';
import { getGroupedPositions } from '../../util/positions';
import SlTargetDialog from '../TradePanel/slAndTargetInputs/SlTargetDialog';
import { OrdersNavButtonsDetailed } from '../postionsWidget';
import OrderRowLivePrice from './OrderRowLivePrice';
import { areOrderRowRenderPropsEqual } from './orderRowMemo';
import { selectOrdersHistory } from '../../selectors/orders';
import {
    isIcebergGroup,
    isNonBasketIcebergLeg,
    OrderOrIcebergGroup,
    getCancellableBasketOrderIds,
    getCancellableIcebergLegIds,
    getIcebergBasketOrderIds,
    isIcebergBatch,
    effectiveOrderFilledQuantity,
} from '../../util/icebergOrders';
import {
    isPlaceholderStrategyGroupName,
    ORDER_TYPES_STRATEGY_AUXILIARY,
    resolveBasketBatchStrategyGroupName,
} from '../../util/orders';

const orderTypeToString = (type: Broker_OrderType): string => {
    switch (type) {
        case Broker_OrderType.MARKET_ORDER: return "MARKET";
        case Broker_OrderType.LIMIT_ORDER: return "LIMIT";
        case Broker_OrderType.STOPLOSS_ORDER: return "SL";
        case Broker_OrderType.STOPLOSS_MARKET_ORDER: return "SL-M";
        default: return "UNKNOWN";
    }
};

export enum ModifyType {
    TARGET = 'TARGET',
    STOPLOSS = 'STOPLOSS'
}

const OrderRow: React.FC<{ 
    order: OrderOrIcebergGroup; 
    hideOrderStatus?: boolean; 
    tabType: OrdersNavButtonsDetailed;
    isNestedInBasket?: boolean;
}> = ({ order: orderOrGroup, hideOrderStatus = false, tabType, isNestedInBasket = false }) => {
    const dispatch = useDispatch()

    // Handle both regular orders and iceberg groups
    const isIceberg = isIcebergGroup(orderOrGroup);
    const icebergGroup = isIceberg ? orderOrGroup : undefined;
    /** Hide per-row cancel only for nested iceberg subgroups; other nested basket legs keep cancel. */
    const hideCancelInNestedRow = isNestedInBasket && isIceberg;

    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const brokers = useSelector(selectBrokers)
    const positions = useSelector(selectPositions);
    const groupBy = useSelector(selectGroupBy);
    const orderHistory = useSelector(selectOrdersHistory);
    
    // For iceberg groups, recalculate totals and determine representative from full order history
    const enhancedIcebergGroup = useMemo(() => {
        if (!isIceberg || !icebergGroup) return undefined;
        
        // Find ALL legs of this iceberg group for THIS SECURITY ONLY from order history (including executed)
        const firstOrder = icebergGroup.allOrders[0];
        if (!firstOrder?.security) return icebergGroup;
        
        const allLegs = orderHistory.filter(o => 
            o.batchId === icebergGroup.batchId && 
            o.basketId === icebergGroup.basketId &&
            o.security?.code === firstOrder.security.code &&
            o.security?.exchange === firstOrder.security.exchange &&
            (firstOrder.isBasket
                ? (
                    o.orderType === OrderType.ICEBERG &&
                    Boolean(o.orderExecutionParams?.icebergNumberOfLegs) &&
                    o.orderExecutionParams?.icebergLegNumber != null
                )
                : isNonBasketIcebergLeg(o))
        );

        const legsToUse = allLegs.length > 0 ? allLegs : icebergGroup.allOrders;
        
        // Recalculate totals for this security only
        const totalFilledQuantity = legsToUse.reduce((sum, o) => sum + effectiveOrderFilledQuantity(o), 0);
        const executedLegs = legsToUse.filter(o => o.status === OrderStatus.EXECUTED).length;
        
        // For iceberg orders, calculate the correct total quantity
        // For basket iceberg orders: each leg has same quantity per security, total = numberOfLegs * legQuantity
        // For single iceberg orders: legs may have different quantities due to splitting, total = sum of all leg quantities
        const firstLeg = legsToUse[0];
        let originalTotalQuantity: number;
        
        if (firstLeg?.orderExecutionParams?.icebergNumberOfLegs) {
            if (firstLeg.isBasket) {
                // Basket iceberg: each leg should have same quantity for same security
                originalTotalQuantity = firstLeg.orderExecutionParams.icebergNumberOfLegs * firstLeg.quantity;
            } else {
                // Single iceberg: legs may have different quantities due to splitting, sum them up
                originalTotalQuantity = legsToUse.reduce((sum, o) => sum + o.quantity, 0);
            }
        } else {
            // Fallback: sum all leg quantities
            originalTotalQuantity = legsToUse.reduce((sum, o) => sum + o.quantity, 0);
        }
        
        // Choose representative: prefer OPEN/PENDING legs, exclude EXECUTED/CANCELLED/FAILED
        const representativeOrder = legsToUse.find(o => 
            o.status === OrderStatus.OPEN || o.status === OrderStatus.PENDING
        ) || legsToUse.find(o => 
            o.status === OrderStatus.PARTIALLY_FILLED
        ) || legsToUse.find(o => 
            o.status !== OrderStatus.CANCELLED && 
            o.status !== OrderStatus.FAILED && 
            o.status !== OrderStatus.EXECUTED
        ) || legsToUse[0];
        
        return {
            ...icebergGroup,
            representativeOrder,
            allOrders: legsToUse,
            totalQuantity: originalTotalQuantity,
            totalFilledQuantity,
            executedLegs
        };
    }, [isIceberg, icebergGroup, orderHistory]);
    
    // Use enhanced group if available
    const finalIcebergGroup = enhancedIcebergGroup || icebergGroup;

    // Get the order to display
    const order = useMemo(() => {
        if (!isIceberg) {
            return orderOrGroup as Order;
        }
        
        if (finalIcebergGroup) {
            // If we have a representative from enhancedIcebergGroup, use it
            if (finalIcebergGroup.representativeOrder) {
                return finalIcebergGroup.representativeOrder;
            }
            
            // Otherwise, pick the best representative from allOrders (excluding executed)
            const allOrders = finalIcebergGroup.allOrders;
            return allOrders.find(o => 
                o.status === OrderStatus.OPEN || o.status === OrderStatus.PENDING
            ) || allOrders.find(o => 
                o.status === OrderStatus.PARTIALLY_FILLED
            ) || allOrders.find(o => 
                o.status !== OrderStatus.CANCELLED && 
                o.status !== OrderStatus.FAILED && 
                o.status !== OrderStatus.EXECUTED
            ) || allOrders[0];
        }
        
        if (icebergGroup?.allOrders?.[0]) {
            return icebergGroup.allOrders[0];
        }
        
        // This should never happen, but provide a safe fallback
        throw new Error('Iceberg group has no orders');
    }, [isIceberg, finalIcebergGroup, icebergGroup, orderOrGroup]);
    
    const securityKey = getSecurityKey(order?.security?.exchange ?? 0, order?.security?.code ?? "")
    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => { if (securityKey) getSecurityAsync(securityKey).then(setSecurity) }, [securityKey])
    const timestamp = order?.exchangeTimestamp || order?.created || 0;
    const date = new Date(timestamp);
    
    const groupedPositions: GroupedPosition[] = useMemo(() => {
        const grouped: Record<string, GroupedPosition> = getGroupedPositions(positions, groupBy)
        return Object.values(grouped)
    }, [positions, groupBy])
    const position = useMemo(() => {
        return groupedPositions.find(p => p.groupId === order?.basketId)
    }, [groupedPositions, order])

    const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const [openSlTarget, setOpenSlTarget] = useState(false)
    const [modifyType, setModifyType] = useState<ModifyType | undefined>(undefined)
    const handleOpenSlTarget = () => {
        if(order.orderType == OrderType.STOP_LOSS){
            setModifyType(ModifyType.STOPLOSS)
        }else{
            setModifyType(ModifyType.TARGET)
        }
        setOpenSlTarget(true)
    }

    const handleCancel = useCallback(async () => {
        if (!order || !order.orderId) return;
        
        // For iceberg orders, cancel only non-executed and non-cancelled legs
        let orderIds: string[];
        let cancellableOrders: Order[];
        
        if (isIceberg && finalIcebergGroup) {
            // Full batch cancel only from a top-level row; nested subgroup uses this security's iceberg legs only.
            if (order.isBasket && order.basketId && order.batchId && !isNestedInBasket) {
                // Basket batch: strategy-only cancels all legs; iceberg batch cancels indexed ladder legs only (SL/TP left to backend).
                if (isIcebergBatch(orderHistory, order.basketId)) {
                    orderIds = getIcebergBasketOrderIds(orderHistory, order.basketId);
                } else {
                    orderIds = getCancellableBasketOrderIds(orderHistory, order.basketId);
                }
                const idSet = new Set(orderIds);
                cancellableOrders = orderHistory.filter(
                    (o) =>
                        o.orderId &&
                        idSet.has(o.orderId)
                );
            } else {
                // Nested/standalone iceberg cancel: include ladder legs + basket-level SL/TP siblings.
                const primaryIds = getCancellableIcebergLegIds(finalIcebergGroup.allOrders);
                const primaryIdSet = new Set(primaryIds);
                const primaryOrders = finalIcebergGroup.allOrders.filter(
                    (o) => o.orderId && primaryIdSet.has(o.orderId)
                );

                const hasLiveOrder = (o: Order) =>
                    o.status !== OrderStatus.EXECUTED &&
                    o.status !== OrderStatus.CANCELLED &&
                    o.status !== OrderStatus.FAILED &&
                    o.status !== OrderStatus.REJECTED;

                const siblingSlTp = orderHistory.filter((o) => {
                    if (!o.orderId) return false;
                    if (!hasLiveOrder(o)) return false;
                    if (!(o.orderType === OrderType.STOP_LOSS || o.orderType === OrderType.TARGET)) return false;
                    if (!order.basketId || o.basketId !== order.basketId) return false;
                    return true;
                });

                const merged = [...primaryOrders, ...siblingSlTp];
                const mergedById = new Map<string, Order>();
                merged.forEach((o) => {
                    if (o.orderId) mergedById.set(o.orderId, o);
                });
                cancellableOrders = [...mergedById.values()];
                orderIds = [...mergedById.keys()];
            }
        } else {
            orderIds = [order.orderId];
            cancellableOrders = [order];
        }
        
        // If no orders to cancel, show message
        if (orderIds.length === 0) {
            commonActions.setApiError(dispatch, {
                error: true,
                message: 'No open orders found',
                errorType: 'info'
            });
            return;
        }
        
        const res = await cancelOrder({
            dispatch,
            body: {
                tradingAccountId: primaryTradingAccount?.accountId ?? "",
                orderId: orderIds,
            }
        });

        const updates: Order[] = [];
        const cancellableById = new Map(
            cancellableOrders.filter((o): o is Order & { orderId: string } => Boolean(o.orderId)).map((o) => [o.orderId, o])
        );
        res.orderResponse.forEach((responseOrder) => {
            if (!responseOrder) return;
            const { successful, commitOrder, errorMessage, order: respOrder } = responseOrder;

            if (!successful) {
                commonActions.setApiError(dispatch, {
                    error: true,
                    message: errorMessage || 'Unable to cancel order',
                    errorType: 'error'
                })
            } else if (commitOrder) {
                const id = respOrder?.orderId || responseOrder.orderId;
                const orderToUpdate = id ? cancellableById.get(id) : undefined;
                if (orderToUpdate) {
                    updates.push({ ...orderToUpdate, status: OrderStatus.CANCELLED });
                }
            }
        });

        if (updates.length > 0) {
            dispatch(updateOrderHistory({ updates }));
        }
    }, [dispatch, order, primaryTradingAccount, isIceberg, finalIcebergGroup, orderHistory, isNestedInBasket]);

    const handleEdit = useCallback(() => {
        const quantity = order.unfilledQuantity || order.quantity;
        if (primaryTradingAccount?.accountId) {
            let mode: QuickTradeData["mode"] = 'regular';

            switch (order.orderType) {
                case OrderType.TRIGGER:
                    mode = "trigger";
                    break;
                case OrderType.STOP_LOSS:
                case OrderType.TARGET:
                    mode = "slOrTarget"
                    break;
                case OrderType.CONDITIONAL_ENTRY:
                case OrderType.CONDITIONAL_EXIT:
                    mode = "regular";
                    break;
            }
            
            // For iceberg orders, check if it's a group
            if (isIceberg && finalIcebergGroup) {
                dispatch(setQuickOrderData({
                    mode: 'iceberg',
                    openDialog: true,
                    type: 'modify',
                    isSquareOffOrder: false,
                    maxQuantity: finalIcebergGroup.totalQuantity, // Original total quantity
                    icebergLegs: finalIcebergGroup.totalLegs, // Original total legs
                    order: {
                        ...order,
                        orderType: OrderType.ICEBERG, // Explicitly set to ICEBERG
                        quantity: finalIcebergGroup.totalQuantity, // Original total quantity
                        // Store the group info for modify operation
                        batchId: finalIcebergGroup.batchId,
                        basketId: finalIcebergGroup.basketId
                    }
                }));
            } else {
                dispatch(setQuickOrderData({
                    mode,
                    openDialog: true,
                    type: 'modify',
                    isSquareOffOrder: false,
                    maxQuantity: quantity,
                    order: {
                        ...order,
                        quantity
                    }
                }));
            }
        }
    }, [dispatch, order, primaryTradingAccount, isIceberg, finalIcebergGroup]);

    const [infoOpen, setInfoOpen] = useState(false)
    const [historyOpen, setHistoryOpen] = useState(false)
    const [tradesOpen, setTradesOpen] = useState(false)

    const currentBroker = useMemo(() => {
        return brokers.find(broker => broker.code === primaryTradingAccount?.brokerCode)
    }, [brokers, primaryTradingAccount?.brokerCode])

    const openInfo = useCallback(() => setInfoOpen(true), [])
    const closeAll = useCallback(() => {
        setInfoOpen(false)
        setHistoryOpen(false)
        setTradesOpen(false)
    }, [])
    const viewHistory = useCallback(() => {
        setInfoOpen(false)
        setHistoryOpen(true)
    }, [])
    const viewTrades = useCallback(() => {
        setInfoOpen(false)
        setTradesOpen(true)
    }, [])
    const backToInfo = useCallback(() => {
        setHistoryOpen(false)
        setTradesOpen(false)
        setInfoOpen(true)
    }, [])

    const displaySymbol = useMemo(() => {
        if (order.isBasket && ORDER_TYPES_STRATEGY_AUXILIARY.includes(order.orderType)) {
            let sg = order.strategyGroup?.trim()
            if (!sg || isPlaceholderStrategyGroupName(sg)) {
                sg =
                    resolveBasketBatchStrategyGroupName(
                        orderHistory,
                        order.basketId,
                        order.batchId
                    ) ??
                    position?.groupName?.trim() ??
                    undefined
            }
            if (sg && !isPlaceholderStrategyGroupName(sg)) return sg
            return position?.groupName ?? security?.symbol ?? 'Basket'
        }
        return security?.symbol
    }, [order, security, position, orderHistory])

    const symbolRowPadding = useMemo(() => {
        return hideOrderStatus ? '5px' : '5px 5px 0'
    }, [hideOrderStatus]);
    const symbolRowBorderBottom = useMemo(() => {
        return hideOrderStatus ? (theme: any) => `1.5px solid ${theme.palette.background.default}` : 'none'
    }, [hideOrderStatus]);

    const isSingleLeg = useMemo(() => {
        if (!order.isBasket) return true;
        if (isNestedInBasket) return true;
        if (!order.batchId || !order.basketId) return true;
        const distinctSecurities = new Set(
            orderHistory
                .filter((to) => to.batchId === order.batchId && to.basketId === order.basketId)
                .map((to) => getSecurityKey(to.security?.exchange ?? 0, to.security?.code ?? ''))
        );
        return distinctSecurities.size <= 1;
    }, [order.isBasket, order.batchId, order.basketId, orderHistory, isNestedInBasket]);

    const strategyAvgPrice = useMemo(() => {
        if (!position || position.children?.length < 1) return 0;
        const { security, quantity, children = [] } = position;
        if (quantity === 0) return 0;
        let avg = 0
        children.forEach(childPos => {
            avg += childPos.avgPrice * childPos.quantity
        });
        return Math.abs(avg/(security.priceMultiplier??100)/ position.quantity)
    }, [position]);

    const icebergFilledVwapRaw = useMemo(() => {
        if (!isIceberg || !finalIcebergGroup) return undefined;
        const legs = finalIcebergGroup.allOrders;
        const filled = legs.reduce((s, o) => s + effectiveOrderFilledQuantity(o), 0);
        if (filled <= 0) return undefined;
        let weighted = 0;
        for (const o of legs) {
            const fq = effectiveOrderFilledQuantity(o);
            if (fq <= 0) continue;
            weighted += (o.averageTradedPrice ?? 0) * fq;
        }
        if (weighted <= 0) return undefined;
        return weighted / filled;
    }, [isIceberg, finalIcebergGroup]);

    const infoDialogOrder = useMemo(() => {
        if (isIceberg && finalIcebergGroup) {
            return {
                ...order,
                quantity: finalIcebergGroup.totalQuantity,
                filledQuantity: finalIcebergGroup.totalFilledQuantity,
            };
        }
        return order;
    }, [isIceberg, finalIcebergGroup, order]);

    const isBasketStrategyAux = useMemo(
        () => order.isBasket && ORDER_TYPES_STRATEGY_AUXILIARY.includes(order.orderType),
        [order.isBasket, order.orderType]
    )

    const avgPrice = useMemo(() => {
        if (isSingleLeg || !isBasketStrategyAux) {
            if (isIceberg && icebergFilledVwapRaw != null && icebergFilledVwapRaw > 0) {
                return toLocaleStringIn(icebergFilledVwapRaw / (security?.priceMultiplier ?? 100));
            }
            return toLocaleStringIn((order.averageTradedPrice ?? 0) / (security?.priceMultiplier ?? 100))
        }
        return strategyAvgPrice
    }, [isSingleLeg, security, order, strategyAvgPrice, isIceberg, icebergFilledVwapRaw, isBasketStrategyAux])
    
    const isSLorTarget = useMemo(() => {
        return ORDER_TYPES_STRATEGY_AUXILIARY.includes(order.orderType);
    }, [order.orderType])

    const isStrategySlOrTp =
        order.isBasket &&
        (order.orderType === OrderType.STOP_LOSS || order.orderType === OrderType.TARGET)
    const hideFillQty = isStrategySlOrTp

    // Add indentation for nested basket items
    const leftPadding = isNestedInBasket ? 2.5 : 1;

    return (
        <>
            <TableRow
                sx={{
                    opacity: [OrderStatus.CANCELLED, OrderStatus.FAILED].includes(order.status ?? 0) ? 0.5 : 1,
                    position: 'relative',
                    '&:hover .edit-icon-order': { display: 'table-cell !important' },
                    '&:hover .edit-icon-replacement': { display: 'none !important' },
                    '&:hover .order-actions': { visibility: 'visible' }
                }}
            >
                <TableCell sx={{ width: '36%', padding: symbolRowPadding, paddingLeft: 0, verticalAlign: 'top', borderBottom: symbolRowBorderBottom }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', px: leftPadding }}>
                        <Typography fontSize={12} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={`${displaySymbol}`}>{displaySymbol} &nbsp;
                        </Typography>
                    </Box>
                </TableCell>

                <TableCell sx={{ width: '5%', padding: symbolRowPadding, textAlign: 'center', verticalAlign: 'top', borderBottom: symbolRowBorderBottom }}>
                    {isSingleLeg || (!isSingleLeg && isSLorTarget) ? 
                    <Typography fontSize="13px" fontWeight={isSLorTarget ? "400" : "500"} sx={{ color: isSLorTarget ? null :  theme => theme.palette[order.transactionType === TransactionType.BUY ? 'buy' : 'sell'].main }}>
                        {order.orderType === OrderType.STOP_LOSS
                            ? 'SL'
                            : order.orderType == OrderType.TARGET
                              ? 'TP'
                              : order.orderType === OrderType.CONDITIONAL_ENTRY
                                ? 'CE'
                                : order.orderType === OrderType.CONDITIONAL_EXIT
                                  ? 'CX'
                                  : order.transactionType === TransactionType.BUY
                                    ? 'B'
                                    : 'S' }
                    </Typography>: '-' 
                    }
                </TableCell>

                <TableCell sx={{ width: '18%', padding: symbolRowPadding, textAlign: 'right', verticalAlign: 'top', borderBottom: symbolRowBorderBottom }}>
                    <Typography fontSize="13px">
                        {hideFillQty
                            ? '-'
                            : <>
                                {isIceberg && finalIcebergGroup ? finalIcebergGroup.totalFilledQuantity : effectiveOrderFilledQuantity(order)}
                                {'/'}
                                {isIceberg && finalIcebergGroup ? finalIcebergGroup.totalQuantity : order.quantity}
                              </>}
                    </Typography>
                </TableCell>

                <TableCell sx={{ width: '23%', padding: symbolRowPadding, textAlign: 'right', verticalAlign: 'top', borderBottom: (theme) => `1.5px solid ${theme.palette.background.default}` }} rowSpan={hideOrderStatus ? 1 : 2}>
                    <Typography fontSize="13px">
                        {
                            Broker_OrderType.STOPLOSS_MARKET_ORDER === order.brokerOrderType ?
                                toLocaleStringIn(order.triggerPrice / (security?.priceMultiplier ?? 100)) :
                                    Broker_OrderType.STOPLOSS_ORDER === order.brokerOrderType ?
                                        toLocaleStringIn(order.price / (security?.priceMultiplier ?? 100)) + ' / ' + toLocaleStringIn((order.triggerPrice ?? 0) / (security?.priceMultiplier ?? 100)) :
                                        toLocaleStringIn(order.price / (security?.priceMultiplier ?? 100))
                        }
                    </Typography>
                </TableCell>

                {tabType === OrdersNavButtonsDetailed.OPEN ? <TableCell sx={{ width: '18%', padding: symbolRowPadding, pr: '2px', textAlign: 'right', verticalAlign: 'top', borderBottom: (theme) => `1.5px solid ${theme.palette.background.default}`, position: 'relative' }} rowSpan={hideOrderStatus ? 1 : 2}>
                    <OrderRowLivePrice
                        securityKey={securityKey}
                        priceMultiplier={security?.priceMultiplier ?? 100}
                        isBasketStrategyAux={isBasketStrategyAux}
                        position={position}
                    />
                    <Box className="order-actions"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            height: '100%',
                            display: 'flex',
                            visibility: 'hidden',
                            background: (theme) => alpha(theme.palette.background.paper, 0.9),
                            alignItems: 'center',
                            zIndex: 2,
                            borderRadius: '4px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <OrderActionsMenu order={order} onEdit={handleEdit} onCancel={handleCancel} onInfo={openInfo} onOpenSlTarget={handleOpenSlTarget} hideCancel={hideCancelInNestedRow} />
                    </Box>
                </TableCell> : <TableCell sx={{ width: '18%', padding: symbolRowPadding, pr: '2px', textAlign: 'right', verticalAlign: 'top', borderBottom: (theme) => `1.5px solid ${theme.palette.background.default}`, position: 'relative' }} rowSpan={hideOrderStatus ? 1 : 2}>
                    <Typography fontSize="13px">{avgPrice}</Typography>
                    <Box className="order-actions"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            height: '100%',
                            display: 'flex',
                            visibility: 'hidden',
                            background: (theme) => alpha(theme.palette.background.paper, 0.9),
                            alignItems: 'center',
                            zIndex: 2,
                            borderRadius: '4px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <OrderActionsMenu order={order} onEdit={handleEdit} onCancel={handleCancel} onInfo={openInfo} onOpenSlTarget={handleOpenSlTarget} hideCancel={hideCancelInNestedRow} />
                    </Box>
                </TableCell>}
            </TableRow>
            {!hideOrderStatus && (<TableRow
                sx={{
                    borderBottom: symbolRowBorderBottom,
                    opacity: [OrderStatus.CANCELLED, OrderStatus.FAILED].includes(order.status ?? 0) ? 0.5 : 1,
                    position: 'relative',
                    '&:hover .edit-icon-order': { display: 'table-cell !important' },
                    '&:hover .edit-icon-replacement': { display: 'none !important' },
                    '&:hover .order-actions': { visibility: 'visible' }
                }}
            >
                <TableCell sx={{ padding: '0 0 5px', verticalAlign: 'top', borderBottom: (theme) => `1.5px solid ${theme.palette.background.default}` }} colSpan={3}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', px: leftPadding }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.3 }}>
                            <Typography fontSize="12px" color="text.secondary">{time}</Typography>
                            <Typography fontSize="12px" marginLeft={'6px'} color="text.secondary" variant="caption">{productToJSON(order.product)}</Typography>
                            <Typography fontSize="12px" marginLeft={'6px'} color="text.secondary" variant="caption">{orderTypeToString(order.brokerOrderType)}</Typography>
                            <span style={{ fontWeight: 500, fontSize: '12px' }} className={
                                order.status === OrderStatus.EXECUTED && !isStrategySlOrTp ? 'green-text' :
                                    order.status === OrderStatus.OPEN ? 'red-text' : ''
                            }>
                                <OrderStatusDisplay status={order.status} secondaryStatus={order.secondaryStatus} executedAsTriggered={isStrategySlOrTp} style={{ fontWeight: 500, fontSize: '12px', marginLeft: '6px', lineHeight: 1.66 }} />
                            </span>
                            {([OrderStatus.FAILED, OrderStatus.REJECTED].includes(order.status ?? 0) && order.message?.length && order.message?.length > 0) ? (
                                <Tooltip title={<Typography fontSize="12px">{order.message}</Typography>} arrow>
                                    <IconButton size="small" disableRipple sx={{ height: 16, width: 16, ml: 0.5, background: (theme) => alpha(theme.palette.background.paper, 0.7) }}>
                                        <InfoOutlined sx={{ height: 16, width: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            ) : null}
                            {isIceberg && finalIcebergGroup && (
                                <Typography fontSize="11px" color="text.secondary" marginLeft={'6px'}>
                                    • {finalIcebergGroup.executedLegs}/{finalIcebergGroup.totalLegs} legs
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </TableCell>
            </TableRow>)}
            <OrderInfoDialog
                open={infoOpen}
                onClose={closeAll}
                onViewTrades={viewTrades}
                onViewHistory={viewHistory}
                order={infoDialogOrder}
                broker={currentBroker}
            />
            {openSlTarget ? (
                <SlTargetDialog
                    open={openSlTarget}
                    position={position}
                    sourceOrder={
                        order.isBasket && (order.orderType === OrderType.STOP_LOSS || order.orderType === OrderType.TARGET)
                            ? order
                            : undefined
                    }
                    onClose={() => { setOpenSlTarget(false); setModifyType(undefined) }}
                    modifyType={modifyType}
                />
            ) : null}
            <OrderHistoryDialog open={historyOpen} onClose={closeAll} onBack={backToInfo} order={order} accountId={primaryTradingAccount?.accountId ?? ''} />
            <TradeHistoryDialog open={tradesOpen} onClose={closeAll} onBack={backToInfo} order={order} accountId={primaryTradingAccount?.accountId ?? ''} />
        </>
    )
};

export default memo(OrderRow, areOrderRowRenderPropsEqual);